import {Dispatch, useCallback, useMemo, useState, useEffect, useRef} from "react";
import styled from "@emotion/styled";
import {useApolloClient} from "@apollo/client";
import {Button, InputGroup, Radio, Spinner, Text} from "@blueprintjs/core";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import InfiniteScroll from "react-infinite-scroll-component";
import MultiSelectDropDown, {MultiSelectDropDownItem} from "../../../components/common/MultiSelectDropDown";
import {ManifestDetailsAssignmentState, ManifestDetailsAssignmentActions} from "./ManifestDetailsReducer";
import {ManifestFilterType} from "../list/components/manifest-filter/ManifestFilterState";
import {useDispatchGroupContext} from "../../../providers/DispatchGroupProvider";
import {useDispatchStationContext} from "../../../providers/DispatchStationProvider";
import {COLORS, EmptyValueStrings} from "../../../constants/constants";
import {
  useSearchDriversEnhancedQuery,
  useSearchVehicleTypesQuery,
  GetDriverTotalManifestsDocument,
  SearchableDriverSortableFields,
  SearchableSortDirection,
  DriverFilterInput,
  Driver
} from "../../../generated/graphql";
import {usePreferenceContext} from "../../../providers/PreferenceProvider";
import VehicleIconResolver from "../../vehicles/VehicleIconResolver";
import {useAssignmentState, useAssignmentDispatch} from "../../../providers/AssignmentProdiver";
import {TDriverEnhanced} from "../../common/types/enhanced";

const ManifestCountCellContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  font-size: 14px;
`;

interface ManifestCountCellProps {
  driverId: number;
  cache: Map<number, number>;
}

const ManifestCountCell: React.FC<ManifestCountCellProps> = ({driverId, cache}) => {
  const cachedCount = cache.get(driverId);

  if (cachedCount !== undefined) {
    return <ManifestCountCellContainer>{cachedCount}</ManifestCountCellContainer>;
  }

  return (
    <ManifestCountCellContainer>
      <Spinner size={15} />
    </ManifestCountCellContainer>
  );
};

const SidebarContainer = styled.div`
  background-color: #f4f5f7;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: "Roboto", sans-serif;
`;

const WarningMessage = styled.div`
  max-width: 520px;
  font-size: 13px;
  line-height: 1.5;
  color: #303030;

  span {
    color: ${COLORS.TEXT_RED};
    font-weight: 600;
  }
`;

const ToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledSearchInput = styled(InputGroup)`
  width: 235px;
  .bp5-input {
    height: 32px;
    border: 1px solid #c3c3c373;
    box-shadow: 0px 1px 3px 0px #0000001a inset;
    border-radius: 4px;
    font-size: 14px;
  }
  .bp5-input-action {
    height: 100%;
    display: flex;
    align-items: center;
    top: 0;
  }
`;

const ListContainer = styled.div`
  flex-grow: 1;
  background: #ffffff;
  border-radius: 4px;
  border: 1px solid #eeeeee;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ListHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 80px 1.5fr 1fr 1fr 100px;
  gap: 8px;
  padding: 0 16px;
  height: 45px;
  min-height: 45px;
  flex-shrink: 0;
  background-color: #f9fafb;
  border-bottom: 2px solid #eeeeee;
  font-weight: 600;
  font-size: 12px;
  color: #4b5563;
  text-transform: uppercase;
  align-items: center;
  scrollbar-gutter: stable;
  overflow-y: hidden;
`;

const ListBody = styled.div`
  overflow-y: auto;
  flex-grow: 1;
  scrollbar-gutter: stable;
`;

const OptionList = styled.div`
  height: 100%;
  width: 100%;
`;

const DriverRow = styled.div<{index: number; isSelected: boolean}>`
  display: grid;
  grid-template-columns: 40px 80px 1.5fr 1fr 1fr 100px;
  gap: 8px;
  padding: 0 16px;
  height: 45px;
  border-bottom: 1px solid #eeeeee;
  background-color: ${(props) => (props.isSelected ? "#0038FF33" : props.index % 2 === 0 ? "#ffffff" : "#fcfcfc")};
  font-size: 14px;
  font-weight: 400;
  align-items: center;

  &:hover {
    background-color: ${(props) => (props.isSelected ? "#0038FF4D" : "#f3f4f6")};
    cursor: pointer;
  }

  .bp5-radio {
    margin: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important; /* Force remove Blueprint default padding-left */
    line-height: 0;
    overflow: visible;
    width: 16px; /* Match indicator size */
    height: 16px;

    .bp5-control-indicator {
      margin: 0 !important; /* Remove all margins */
    }

    input:checked ~ .bp5-control-indicator {
      background-color: #14305a;
      box-shadow: none;
    }
  }
`;

const Col = styled.div`
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  min-width: 0;
`;

const RadioCol = styled(Col)`
  justify-content: center;
  overflow: visible;
`;

const StyledGrayText = styled(Text)`
  color: #797979;
  width: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 12px;
  color: #797979;
  font-size: 14px;
  font-style: italic;
`;

const StyledVehicleIcon = styled(FontAwesomeIcon)`
  margin: auto;
  max-height: 100%;
  max-width: 100%;
`;

const VehicleIconContainer = styled.div`
  height: 35px;
  width: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledClearButton = styled(Button)`
  &.bp5-button.bp5-minimal {
    min-height: 24px;
    min-width: 24px;
    padding: 0;
    margin-right: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const DRIVER_LIST_COLUMNS = ["Vehicle", "Driver Name", "Group", "Driver Code", "Manifests"];
const FETCH_DRIVER_LIMIT = 100;

type ManifestDetailsDriverSelectorProps = {
  manifestDetailsAssignmentState: ManifestDetailsAssignmentState;
  manifestDetailsAssignmentDispatch: Dispatch<ManifestDetailsAssignmentActions>;
};

const ManifestDetailsDriverSelector = ({manifestDetailsAssignmentState}: ManifestDetailsDriverSelectorProps) => {
  const {driverGroups} = useDispatchGroupContext();
  const {tenantPreferences} = usePreferenceContext();
  const {selectedDispatchStations} = useDispatchStationContext();
  const client = useApolloClient();

  const manifestCountCacheRef = useRef(new Map<number, number>());
  const isFetchingRef = useRef(false);
  const onLoadMoreRef = useRef<(currentList?: TDriverEnhanced[]) => void>();

  // --- State ---
  const [isFiltering, setIsFiltering] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [driverItems, setDriverItems] = useState<TDriverEnhanced[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalRawCount, setTotalRawCount] = useState(0);

  const [isInitialized, setIsInitialized] = useState(false);

  const assignmentState = useAssignmentState();
  const assignmentDispatch = useAssignmentDispatch();
  const {splitJobDriver} = assignmentState;

  // Clear selection on mount to ensure a fresh state for every reassignment session
  useEffect(() => {
    if (!isInitialized) {
      assignmentDispatch({type: "SetSplitJobDriver", payload: undefined});
      setIsInitialized(true);
    }
  }, [isInitialized, assignmentDispatch]);

  // --- Selectors & Filters (Decoupled) ---
  const [localSelectedGroupIds, setLocalSelectedGroupIds] = useState<number[]>(() => {
    const filterState = manifestDetailsAssignmentState.assignDriverManifestFilterState;
    if (!filterState) return [];
    const groupFilter = filterState.manifestFilters.get(ManifestFilterType.DRIVER_GROUP);
    return groupFilter && groupFilter.value ? (Array.from(groupFilter.value) as number[]) : [];
  });

  // Track station changes to avoid infinite loops
  const prevStationsRef = useRef(selectedDispatchStations);

  // Reset state when dispatch station changes
  useEffect(() => {
    if (selectedDispatchStations !== prevStationsRef.current) {
      prevStationsRef.current = selectedDispatchStations;

      // If stations are cleared (All Stations), keep the previous group selection
      if (selectedDispatchStations.size === 0) return;

      // Smart Group Filter: Keep only groups that are still valid for the new station set
      // we only update local state now
      const allValidGroupIdsForNewStations = new Set(
        Array.from(selectedDispatchStations.values()).flatMap((ds) => Array.from(ds.driverGroups || []))
      );

      setLocalSelectedGroupIds((prev) => {
        const newValidSelection = prev.filter((id) => allValidGroupIdsForNewStations.has(id));
        return newValidSelection;
      });
    }
  }, [selectedDispatchStations]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 800);
    return () => clearTimeout(timer);
  }, [query]);

  const driverFilter = useMemo((): DriverFilterInput => {
    const filter: DriverFilterInput = {
      driver: {
        driverStatus: {eq: "A"}
      }
    };

    // Use local selected group IDs specifically if they exist
    // Otherwise fallback to all available groups from current context (which is station-filtered)
    let filterGroupIds: number[] = [];
    if (localSelectedGroupIds.length > 0) {
      filterGroupIds = localSelectedGroupIds;
    } else {
      filterGroupIds = driverGroups
        .map((g) => g.dispatchGroupId)
        .filter((id): id is number => id !== undefined && id !== null);
    }

    // Deduplicate and sort for stringification stability
    const uniqueGroupIds = Array.from(new Set(filterGroupIds)).sort((a, b) => a - b);

    if (uniqueGroupIds.length > 0) {
      filter.driver!.or = uniqueGroupIds.map((id) => ({
        dispatchGroupId: {eq: id}
      }));
    }

    if (debouncedQuery.length > 0) {
      filter.multiFieldWildcard = {
        wildcard: `*${debouncedQuery}*`,
        caseInsensitive: true,
        fields: ["name.keyword", "driverCode.keyword"]
      };
    }

    return filter;
  }, [localSelectedGroupIds, debouncedQuery, driverGroups]);

  const processAndFilterDrivers = useCallback(
    async (rawItems: Driver[], currentList: Driver[] = []) => {
      setIsFiltering(true);
      const results = await Promise.all(
        rawItems.map(async (driver) => {
          const driverId = driver.driverId;
          if (!driverId) return {driver, count: 0};

          if (manifestCountCacheRef.current.has(driverId)) {
            return {driver, count: manifestCountCacheRef.current.get(driverId)};
          }

          try {
            const {data} = await client.query({
              query: GetDriverTotalManifestsDocument,
              variables: {driverId}
            });
            const count = data?.getDriverTotalManifests?.manifestCount ?? 0;
            manifestCountCacheRef.current.set(driverId, count);
            return {driver, count};
          } catch {
            return {driver, count: 0};
          }
        })
      );

      const filtered = results.filter((r) => (r.count ?? 0) > 0).map((r) => r.driver);
      const newList = [...currentList, ...filtered];

      setDriverItems(newList);
      setIsFiltering(false);

      // Auto-fill logic: if less than 15 items and backend has more, fetch more
      if (newList.length < 15 && rawItems.length === FETCH_DRIVER_LIMIT && !isFetchingRef.current) {
        onLoadMoreRef.current?.(newList);
      }
    },
    [client]
  );

  // --- Data Fetching ---
  const {fetchMore, loading} = useSearchDriversEnhancedQuery({
    variables: {
      filter: driverFilter,
      limit: FETCH_DRIVER_LIMIT,
      offset: 0,
      sort: [
        {
          field: SearchableDriverSortableFields.Name,
          direction: SearchableSortDirection.Asc
        }
      ]
    },
    onCompleted: (data) => {
      const items = (data?.searchDriversEnhanced?.items || []) as Driver[];
      setHasMore(items.length >= FETCH_DRIVER_LIMIT);
      setTotalRawCount(items.length);
      processAndFilterDrivers(items, []);
    }
  });

  // Reset list when filter or search changes to trigger "Big Loading"
  // Use a stringified version to only reset if the filter VALUES actually change
  const lastFilterStrRef = useRef<string>("");
  useEffect(() => {
    const currentFilterStr = JSON.stringify(driverFilter);
    if (currentFilterStr !== lastFilterStrRef.current) {
      lastFilterStrRef.current = currentFilterStr;
      setDriverItems([]);
      setHasMore(true);
      setTotalRawCount(0);
    }
  }, [driverFilter]);

  const {data: vehicleTypesData} = useSearchVehicleTypesQuery({
    variables: {
      limit: 1000
    }
  });

  const vehicleTypes = useMemo(() => vehicleTypesData?.searchVehicleTypes?.items || [], [vehicleTypesData]);

  const onLoadMore = useCallback(
    (currentList: Driver[] = driverItems) => {
      if (loading || isFiltering || isFetchingRef.current || !hasMore) return;

      isFetchingRef.current = true;
      fetchMore({
        variables: {
          offset: totalRawCount
        }
      })
        .then((result) => {
          const newItems = (result.data?.searchDriversEnhanced?.items || []) as Driver[];
          setHasMore(newItems.length >= FETCH_DRIVER_LIMIT);
          setTotalRawCount((prev) => prev + newItems.length);
          processAndFilterDrivers(newItems, currentList);
        })
        .finally(() => {
          isFetchingRef.current = false;
        });
    },
    [driverItems, fetchMore, loading, isFiltering, hasMore, processAndFilterDrivers, totalRawCount]
  );

  onLoadMoreRef.current = onLoadMore;

  // --- Derived State ---
  const isInitialLoading = (loading || isFiltering) && driverItems.length === 0;
  const isEmptyCloud = !loading && !isFiltering && driverItems.length === 0;

  // --- Actions ---
  const onSelectDriver = useCallback(
    (driver: Driver) => {
      assignmentDispatch({
        type: "SetSplitJobDriver",
        payload: driver
      });
    },
    [assignmentDispatch]
  );

  const driverGroupItems = useMemo(() => {
    const map = new Map<number, MultiSelectDropDownItem>();
    if (driverGroups.length > 0) {
      const sortedGroups = [...driverGroups].sort((a, b) =>
        (a.dispatchGroup || "").localeCompare(b.dispatchGroup || "")
      );
      sortedGroups.forEach((group) => {
        if (group.dispatchGroupId !== undefined && group.dispatchGroupId !== null) {
          map.set(group.dispatchGroupId, {
            id: group.dispatchGroupId,
            name: group.dispatchGroup || "Unknown",
            selected: localSelectedGroupIds.includes(group.dispatchGroupId)
          });
        }
      });
    }
    return map;
  }, [driverGroups, localSelectedGroupIds]);

  const onSelectDriverGroup = useCallback((key: number) => {
    setLocalSelectedGroupIds((prev) => Array.from(new Set([...prev, key])));
  }, []);

  const onUnselectDriverGroup = useCallback((key: number) => {
    setLocalSelectedGroupIds((prev) => prev.filter((id) => id !== key));
  }, []);

  const onUnselectAllDriverGroup = useCallback(() => {
    setLocalSelectedGroupIds([]);
  }, []);

  // --- Render Helpers ---
  const renderVehicleIcon = (vehicleTypeId: number | null | undefined) => {
    const vehicleType = vehicleTypes.find((vt) => vt?.vehicleTypeId === vehicleTypeId);
    return (
      <VehicleIconContainer>
        <StyledVehicleIcon
          size={"lg"}
          color={"#4C505E"}
          icon={VehicleIconResolver(vehicleType?.description, tenantPreferences)}
          title={vehicleType?.description ?? EmptyValueStrings.unknown}
        />
      </VehicleIconContainer>
    );
  };

  const mappedDriverItems = useMemo(() => {
    return driverItems.map((driver) => {
      const group = driverGroups.find((g) => g.dispatchGroupId === driver.dispatchGroupId);
      return {
        ...driver,
        dispatchGroup: group || driver.dispatchGroup
      };
    });
  }, [driverItems, driverGroups]);

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <LoadingContainer>
          <Spinner size={20} />
          <span>Loading drivers...</span>
        </LoadingContainer>
      );
    }

    if (isEmptyCloud) {
      return <LoadingContainer>No drivers found!</LoadingContainer>;
    }

    return (
      <InfiniteScroll
        next={onLoadMore}
        hasMore={hasMore}
        loader={
          <LoadingContainer>
            <Spinner size={20} />
            <span>Loading more drivers...</span>
          </LoadingContainer>
        }
        scrollableTarget="driver-list-body"
        dataLength={mappedDriverItems.length}
      >
        <OptionList>
          {mappedDriverItems.map((driver, index: number) => {
            const isSelected = splitJobDriver?.driverId === driver.driverId;
            return (
              <DriverRow
                key={`${driver?.driverId}-${index}`}
                index={index}
                isSelected={isSelected}
                onClick={() => onSelectDriver(driver)}
              >
                <RadioCol>
                  <Radio checked={isSelected} readOnly />
                </RadioCol>
                <Col>{renderVehicleIcon(driver?.vehicleTypeId)}</Col>
                <Col>
                  <Text ellipsize title={driver?.name ?? EmptyValueStrings.unknown} style={{width: "100%"}}>
                    {driver?.name ?? EmptyValueStrings.unknown}
                  </Text>
                </Col>
                <Col>
                  <Text
                    ellipsize
                    title={driver?.dispatchGroup?.dispatchGroup ?? EmptyValueStrings.unknown}
                    style={{width: "100%"}}
                  >
                    {driver?.dispatchGroup?.dispatchGroup ?? EmptyValueStrings.unknown}
                  </Text>
                </Col>
                <Col>
                  <StyledGrayText ellipsize title={driver?.driverCode ?? EmptyValueStrings.unknown}>
                    {driver?.driverCode ?? EmptyValueStrings.unknown}
                  </StyledGrayText>
                </Col>
                <Col>
                  <ManifestCountCell driverId={driver?.driverId ?? 0} cache={manifestCountCacheRef.current} />
                </Col>
              </DriverRow>
            );
          })}
        </OptionList>
      </InfiniteScroll>
    );
  };

  return (
    <SidebarContainer>
      <WarningMessage>
        <span>NOTE:</span> Only drivers with already created manifest(s) are displayed.
        <br />
        Please be aware that if the driver has multiple manifests that are incomplete, it&apos;s crucial to verify that
        the split job has been assigned to the correct manifest.
      </WarningMessage>

      <ToolbarContainer>
        <MultiSelectDropDown
          text={"Groups"}
          testIdPrefix={"driver-groups"}
          items={driverGroupItems}
          onSelected={onSelectDriverGroup}
          onUnselected={onUnselectDriverGroup}
          clearFilter
          onUnselectedAll={onUnselectAllDriverGroup}
        />
        <StyledSearchInput
          leftIcon="search"
          placeholder="Search By Name or ID"
          value={query}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          rightElement={
            query ? (
              <StyledClearButton icon="cross" minimal onClick={() => setQuery("")} data-testid="search-clear-button" />
            ) : undefined
          }
        />
      </ToolbarContainer>

      <ListContainer>
        <ListHeader>
          <RadioCol />
          {DRIVER_LIST_COLUMNS.map((col) => (
            <Col key={col}>
              <Text ellipsize title={col} style={{width: "100%"}}>
                {col}
              </Text>
            </Col>
          ))}
        </ListHeader>
        <ListBody id="driver-list-body">{renderContent()}</ListBody>
      </ListContainer>
    </SidebarContainer>
  );
};

export default ManifestDetailsDriverSelector;
export type {ManifestDetailsDriverSelectorProps};
